import React from 'react';
import { Link } from 'react-router-dom';
import {
  WhatsAppOutlined,
  ShopOutlined,
  BarcodeOutlined,
  BarChartOutlined,
  NotificationOutlined,
  SendOutlined,
  FileExcelOutlined,
  FacebookOutlined,
  CheckOutlined,
  ArrowRightOutlined,
  DollarOutlined,
  EnvironmentOutlined
} from '@ant-design/icons';
import { FEATURE_MODULES } from '../constants/publicFeatures';
import { PLANS, getPlanPricing, getPlanLink } from '../constants/plans';
// Nouveau design citron / noir / blanc (préfixe "lp-")
import './LandingPageCitron.css';

/* ------------------------------------------------------------------ */
/* Données de la page : chaque section est générée à partir de ces     */
/* tableaux. Seules des informations vérifiables y figurent.           */
/* ------------------------------------------------------------------ */

// Chiffres clés affichés dans la bande noire (issus de la configuration réelle)
const STATS = [
  { valeur: '14 jours', libelle: 'd\'essai gratuit, sans engagement' },
  { valeur: '3', libelle: 'Applications : backoffice, caisse et vitrine en ligne' },
  { valeur: '4', libelle: 'Fournisseurs WhatsApp API pris en charge' },
  { valeur: 'XOF', libelle: 'Tarifs et montants en franc CFA' }
];

// Étapes du parcours
const STEPS = [
  { titre: 'Connectez WhatsApp', texte: 'Branchez votre fournisseur WhatsApp API : WhatChimp, WATI, WasenderAPI ou AiSensy.' },
  { titre: 'Configurez votre boutique', texte: 'Ajoutez vos produits, vos zones de livraison, vos livreurs et vos modèles de message.' },
  { titre: 'Recevez et suivez', texte: 'Commandes, livraisons, stocks et dépenses sont suivis depuis un seul tableau de bord.' }
];

// Outils connectés
const INTEGRATIONS = [
  { nom: 'WhatsApp API', icon: <ShopOutlined /> },
  { nom: 'Telegram (alertes)', icon: <SendOutlined /> },
  { nom: 'Meta Ads', icon: <NotificationOutlined /> },
  { nom: 'Pixel Facebook', icon: <FacebookOutlined /> },
  { nom: 'Google Sheets', icon: <FileExcelOutlined /> },
  { nom: 'Caisse (POS)', icon: <BarcodeOutlined /> },
  { nom: 'Analytics', icon: <BarChartOutlined /> }
];

// Particularités du marché local prises en charge
const LOCAL_POINTS = [
  { icon: <DollarOutlined />, titre: 'Franc CFA', texte: 'Prix, commandes, dépenses et abonnements exprimés en XOF.' },
  { icon: <EnvironmentOutlined />, titre: 'Livraison par zones', texte: 'Zones, jours de livraison et livreurs configurables pour chaque boutique.' },
  { icon: <BarcodeOutlined />, titre: 'Paiements locaux', texte: 'La caisse accepte espèces, carte et mobile money.' }
];

// Titre de section réutilisable (pastille citron + titre + description)
const SectionHead = ({ tag, titre, texte, centre = true }) => (
  <div className={`lp-section-head ${centre ? 'lp-center' : ''}`}>
    <span className="lp-pill">{tag}</span>
    <h2 className="lp-h2">{titre}</h2>
    {texte && <p className="lp-lead">{texte}</p>}
  </div>
);

const LandingPage = () => {
  return (
    <div className="lp-root">
      <main>
        {/* ===== Hero ===== */}
        <section className="lp-hero">
          <div className="lp-container lp-hero-grid">
            <div>
              <span className="lp-pill"><WhatsAppOutlined /> Vendez là où sont vos clients</span>
              <h1 className="lp-h1">
                Gérez vos ventes WhatsApp, <span className="lp-mark">de la commande à la livraison</span>
              </h1>
              <p className="lp-lead">
                Mercuri Shop réunit commandes, livraisons, stocks, caisse et vitrine en ligne.
                Un assistant IA vous aide à analyser votre activité.
              </p>
              <div className="lp-hero-cta">
                <Link to="/register" className="lp-btn lp-btn-accent lp-btn-lg">
                  Essai gratuit 14 jours <ArrowRightOutlined />
                </Link>
                <Link to="/contact" className="lp-btn lp-btn-outline lp-btn-lg">Demander une démo</Link>
              </div>
              <p className="lp-proof">Essai gratuit de 14 jours, 30 points IA offerts</p>
            </div>

            {/* Maquette : suivi d'une commande */}
            <div className="lp-mock" aria-hidden="true">
              <div className="lp-mock-card lp-mock-chat">
                <div className="lp-mock-bar"><span className="lp-dot" /> Exemple de suivi de commande</div>
                {[
                  { etat: 'Commande confirmée', detail: '2 × Robe Wax · 24 000 XOF', fait: true },
                  { etat: 'Paiement reçu', detail: 'Mobile money', fait: true },
                  { etat: 'Livreur assigné', detail: 'Zone Cocody', fait: true },
                  { etat: 'En cours de livraison', detail: 'Message client programmé', fait: false }
                ].map((etape) => (
                  <div key={etape.etat} className={`lp-track ${etape.fait ? 'lp-track-done' : ''}`}>
                    <span className="lp-track-dot">{etape.fait && <CheckOutlined />}</span>
                    <span><strong>{etape.etat}</strong><small>{etape.detail}</small></span>
                  </div>
                ))}
              </div>
              <div className="lp-mock-card lp-mock-stat lp-mock-stat-a">
                <span>Message de livraison</span><strong>09:00</strong>
              </div>
              <div className="lp-mock-card lp-mock-stat lp-mock-stat-b">
                <span>Points IA offerts</span><strong>30</strong>
              </div>
            </div>
          </div>
        </section>

        {/* ===== Chiffres clés ===== */}
        <section className="lp-stats">
          <div className="lp-container lp-stats-grid">
            {STATS.map((stat) => (
              <div key={stat.valeur} className="lp-stat">
                <strong>{stat.valeur}</strong>
                <span>{stat.libelle}</span>
              </div>
            ))}
          </div>
        </section>

        {/* ===== Fonctionnalités ===== */}
        <section id="fonctionnalites" className="lp-section">
          <div className="lp-container">
            <SectionHead
              tag="Fonctionnalités"
              titre="Tout votre commerce, au même endroit"
              texte="Des modules qui travaillent ensemble pour gérer vos commandes, vos livraisons et votre boutique."
            />
            <div className="lp-bento">
              {FEATURE_MODULES.map((feature) => (
                <article key={feature.key} className={`lp-feature ${feature.large ? 'lp-feature-large' : ''}`}>
                  <span className="lp-feature-icon">{feature.icon}</span>
                  <h3>{feature.titre}</h3>
                  <p>{feature.resume}</p>
                  {feature.tags && (
                    <div className="lp-tags">
                      {feature.tags.map((tag) => <span key={tag} className="lp-tag">{tag}</span>)}
                    </div>
                  )}
                </article>
              ))}
            </div>
            <p className="lp-center lp-plans-link"><Link to="/features">Voir le détail des fonctionnalités <ArrowRightOutlined /></Link></p>
          </div>
        </section>

        {/* ===== Étapes ===== */}
        <section id="etapes" className="lp-section lp-section-soft">
          <div className="lp-container">
            <SectionHead tag="Comment ça marche" titre="Opérationnel en trois étapes" />
            <div className="lp-steps">
              {STEPS.map((etape, index) => (
                <div key={etape.titre} className="lp-step">
                  <span className="lp-step-num">{index + 1}</span>
                  <h3>{etape.titre}</h3>
                  <p>{etape.texte}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== Intégrations ===== */}
        <section id="integrations" className="lp-section">
          <div className="lp-container">
            <SectionHead
              tag="Intégrations"
              titre="Branché sur vos outils"
              texte="WhatsApp, Telegram, Meta, Google Sheets : connectez vos outils depuis les paramètres, sans code."
            />
            <div className="lp-integrations">
              {INTEGRATIONS.map((outil) => (
                <div key={outil.nom} className="lp-integration">
                  <span className="lp-integration-icon">{outil.icon}</span>
                  {outil.nom}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== Marché local ===== */}
        <section className="lp-section lp-section-soft">
          <div className="lp-container">
            <SectionHead tag="Pensé pour ici" titre="Adapté au commerce en Côte d'Ivoire" />
            <div className="lp-steps">
              {LOCAL_POINTS.map((point) => (
                <div key={point.titre} className="lp-step">
                  <span className="lp-feature-icon">{point.icon}</span>
                  <h3>{point.titre}</h3>
                  <p>{point.texte}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== Tarifs (données issues de constants/plans.js) ===== */}
        <section id="tarifs" className="lp-section">
          <div className="lp-container">
            <SectionHead
              tag="Tarifs"
              titre="Simple et transparent"
              texte="Commencez par l'essai gratuit de 14 jours, puis choisissez l'offre qui correspond à votre activité."
            />
            <div className="lp-plans">
              {PLANS.map((plan) => {
                const prix = getPlanPricing(plan);
                return (
                  <div key={plan.key} className={`lp-plan ${plan.popular ? 'lp-plan-pop' : ''}`}>
                    {plan.popular && <span className="lp-plan-badge">Le plus populaire</span>}
                    <h3>{plan.name}</h3>
                    <div className="lp-plan-price"><strong>{prix.price}</strong><span>{prix.period || 'Essai 14 jours'}</span></div>
                    <ul>
                      {plan.features.slice(0, 5).map((avantage) => (
                        <li key={avantage}><CheckOutlined /> {avantage}</li>
                      ))}
                    </ul>
                    <Link to={getPlanLink(plan)} className={`lp-btn ${plan.popular ? 'lp-btn-accent' : 'lp-btn-outline'}`}>
                      {plan.buttonText}
                    </Link>
                  </div>
                );
              })}
            </div>
            <p className="lp-center lp-plans-link"><Link to="/pricing">Comparer tous les plans <ArrowRightOutlined /></Link></p>
          </div>
        </section>

        {/* ===== Appel à l'action final ===== */}
        <section className="lp-cta">
          <div className="lp-container lp-cta-inner">
            <h2>Démarrez votre essai gratuit</h2>
            <p>14 jours pour tester Mercuri Shop avec les fonctionnalités de l'offre Essentiel.</p>
            <div className="lp-hero-cta lp-center">
              <Link to="/register" className="lp-btn lp-btn-accent lp-btn-lg">Essai gratuit 14 jours</Link>
              <Link to="/contact" className="lp-btn lp-btn-light lp-btn-lg">Demander une démo</Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default LandingPage;
